import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/site";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "price_asc", label: "Price · Low to High" },
  { key: "price_desc", label: "Price · High to Low" },
  { key: "newest", label: "Newest" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const maxPrice = params.get("max");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    const q = category !== "All" ? { category } : {};
    api
      .get("/products", { params: q })
      .then((d) => setProducts(d.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    let p = [...products];
    if (maxPrice) p = p.filter((x) => x.price < parseFloat(maxPrice));
    if (query.trim()) p = p.filter((x) => x.name.toLowerCase().includes(query.trim().toLowerCase()));
    if (sort === "price_asc") p.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") p.sort((a, b) => b.price - a.price);
    if (sort === "newest") p.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (sort === "featured") p.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return p;
  }, [products, sort, query, maxPrice]);

  const setCategory = (c) => setParams(c === "All" ? {} : { category: c });

  return (
    <main className="pt-24 lg:pt-32">
      <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 pb-12" data-testid="shop-page">
        <Reveal>
          <p className="overline-tag">The Atelier</p>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
            <h1 className="font-display text-5xl sm:text-6xl font-semibold text-ink">
              {category === "All" ? (maxPrice ? `Under ₹${Number(maxPrice).toLocaleString("en-IN")}` : "All Jewels") : category}
            </h1>
            <span className="font-mono text-xs text-ink3" data-testid="shop-count">
              {loading ? "Curating…" : `${sorted.length} ${sorted.length === 1 ? "piece" : "pieces"}`}
            </span>
          </div>
        </Reveal>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="shop-filters">
            {["All", ...CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`border px-4 py-2 font-cinzel text-[10px] font-semibold uppercase transition-all duration-300 ${
                  category === c
                    ? "border-gold bg-gold text-[#241A08]"
                    : "border-line text-ink2 hover:border-gold hover:text-gold"
                }`}
                style={{ letterSpacing: "0.18em" }}
                data-testid={`filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex w-full items-center gap-3 sm:w-auto">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search jewellery…"
              className="field min-w-0 flex-1 !py-2.5 sm:!w-56 sm:flex-none"
              data-testid="shop-search"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="shrink-0 border border-line bg-surface px-4 py-2.5 font-mono text-xs text-ink2 outline-none focus:border-gold"
              data-testid="shop-sort"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-bg2 animate-pulse border border-line" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="py-24 text-center border border-dashed border-line" data-testid="shop-empty">
              <p className="font-display text-3xl text-ink2">No pieces here yet</p>
              <p className="mt-2 text-sm text-ink3">This collection is being crafted — try another, or enquire with the concierge.</p>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {sorted.map((p, i) => (
                  <ProductCard key={p._id} product={p} index={i} />
                ))}
              </div>
            </AnimatePresence>
          )}
        </div>
      </section>
    </main>
  );
}
